import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { useAnalysisGenerationActions } from 'redux/actions/AnalysisGenerations'
import {
  selectAnalysisGenerating,
  selectAnalysisGenerationsFetched,
  selectCompletedAnalysisGenerations,
  selectLatestAnalysisGenerations
} from 'redux/selectors/AnalysisGenerations'

import type { TAnalysisTypeDto } from '@homepal/api-contract'

import { AnalysisGeneration } from 'types/AnalysisGeneration'

const GENERATION_POLL_INTERVAL_MS = 5000

export const useAnalysisTypeGenerating = () =>
  useSelector(selectAnalysisGenerating)

export const useLatestAnalysisTypeGenerations = () =>
  useSelector(selectLatestAnalysisGenerations)

export const useAnalysisTypeGenerationsFetched = () =>
  useSelector(selectAnalysisGenerationsFetched)

export const useCompletedAnalysisGenerations = () =>
  useSelector(selectCompletedAnalysisGenerations)

/**
 * Restores in-flight generations after a page refresh and polls each one
 * until it reaches a terminal status. Mounted once, app-wide: finished runs
 * land in the store's `completed`.
 */
export const useGenerationPolling = () => {
  const generating = useAnalysisTypeGenerating()
  const fetched = useAnalysisTypeGenerationsFetched()
  const { tryGetGenerations, tryPollGeneration } =
    useAnalysisGenerationActions()

  // Generations survive a page refresh — they are jobs in the backend.
  // The overview restores the latest outcome per analysis type and the
  // loading state for anything still in flight.
  useEffect(() => {
    if (!fetched) {
      tryGetGenerations()
    }
  }, [fetched, tryGetGenerations])

  useEffect(() => {
    const generationIds = Object.values(generating)

    if (generationIds.length === 0) {
      return undefined
    }

    const interval = setInterval(() => {
      generationIds.forEach((generationId) => tryPollGeneration(generationId))
    }, GENERATION_POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [generating, tryPollGeneration])
}

export type TAnalysisGenerationState = {
  /** The latest generation for the analysis type, finished or not. */
  generation: AnalysisGeneration | null
  running: boolean
  /** True when the trigger request itself failed. */
  triggerFailed: boolean
  cancel: () => void
  generate: () => void
}

/**
 * The generation state of a single analysis type. The polling that keeps it up
 * to date runs app-wide, see useGenerationPolling.
 */
export const useAnalysisTypeGeneration = (
  analysisType: TAnalysisTypeDto | null,
  onCancelled?: (generation: AnalysisGeneration) => void
): TAnalysisGenerationState => {
  const analysisTypeId = analysisType?.id ?? null

  const generating = useAnalysisTypeGenerating()
  const latest = useLatestAnalysisTypeGenerations()
  const { tryStartGeneration, tryCancelGeneration } =
    useAnalysisGenerationActions()

  const [triggerFailed, setTriggerFailed] = useState(false)

  return {
    generation: analysisTypeId ? latest[analysisTypeId] ?? null : null,
    running: !!analysisTypeId && !!generating[analysisTypeId],
    triggerFailed,
    cancel: () => {
      const generationId = analysisTypeId
        ? generating[analysisTypeId]
        : undefined

      if (generationId) {
        tryCancelGeneration(generationId, onCancelled)
      }
    },
    generate: () => {
      if (analysisTypeId) {
        setTriggerFailed(false)
        tryStartGeneration(analysisTypeId, setTriggerFailed)
      }
    }
  }
}

/**
 * A rerun of this analysis in flight: the latest run of its analysis type
 * that overwrites it in place. Survives a page refresh like any other run.
 * No analysis type once it is retired, and then nothing can rerun it.
 */
export const useAnalysisRerun = (
  analysis: { analysis_type_id: string | null; id: string } | null
) => {
  const generating = useAnalysisTypeGenerating()
  const latest = useLatestAnalysisTypeGenerations()
  const { tryCancelGeneration } = useAnalysisGenerationActions()

  const analysisTypeId = analysis?.analysis_type_id ?? null
  const latestRun = analysisTypeId ? latest[analysisTypeId] ?? null : null
  const running =
    !!analysisTypeId &&
    !!generating[analysisTypeId] &&
    latestRun?.rerun_analysis_id === analysis?.id

  return {
    generation: running ? latestRun : null,
    running,
    cancel: (onCancelled?: (generation: AnalysisGeneration) => void) => {
      if (running && latestRun) {
        tryCancelGeneration(latestRun.id, onCancelled)
      }
    }
  }
}
